import type { ApolloError } from '@apollo/client';
import type { QueryResult } from '@apollo/client/react';
import Button from '@aurora/shared-client/components/common/Button/Button';
import { ButtonVariant } from '@aurora/shared-client/components/common/Button/enums';
import { IconColor, IconSize } from '@aurora/shared-client/components/common/Icon/enums';
import Icon from '@aurora/shared-client/components/common/Icon/Icon';
import AppContext from '@aurora/shared-client/components/context/AppContext/AppContext';
import QuiltContext from '@aurora/shared-client/components/context/QuiltContext';
import TenantContext from '@aurora/shared-client/components/context/TenantContext';
import Icons from '@aurora/shared-client/icons';
import useEndUserRoutes from '@aurora/shared-client/routes/useEndUserRoutes';
import type { QuiltFragment } from '@aurora/shared-generated/types/graphql-types';
import { EndUserComponent, EndUserPages } from '@aurora/shared-types/pages/enums';
import {
  merge,
  UndefinedValueMergeBehavior
} from '@aurora/shared-utils/helpers/objects/ObjectHelper';
import { getLog } from '@aurora/shared-utils/log';
import dynamic from 'next/dynamic';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import type {
  FeaturedIdeaStatusPoliciesQuery,
  FeaturedIdeaStatusPoliciesQueryVariables,
  FeaturedIdeaStatusesWidgetQuery,
  FeaturedIdeaStatusesWidgetQueryVariables
} from '../../../../types/graphql-types';
import EditableWidget from '../../../common/Widget/EditableWidget';
import type {
  InstanceScopedWidgetFC,
  InstanceScopedWidgetProps
} from '../../../common/Widget/types';
import type { EditContextInterface } from '../../../context/EditContext/EditContext';
import EditContext from '../../../context/EditContext/EditContext';
import useTranslation from '../../../useTranslation';
import FeaturedIdeaStatusesList from '../FeaturedIdeaStatusesList/FeaturedIdeaStatusesList';
import localStyles from './FeaturedIdeaStatusesWidget.module.pcss';
import type { FeaturedIdeaStatusesWidgetConfigurableProps } from './types';
import { ListVariant } from '@aurora/shared-client/components/common/List/enums';
import { PagerVariant } from '@aurora/shared-client/components/common/Pager/enums';
import { IdeaStatusViewVariant } from '../../../../types/enums';
import { PanelType } from '@aurora/shared-client/components/common/Panel/enums';
import useQueryWithTracing from '@aurora/shared-client/components/useQueryWithTracing';
import featuredIdeaStatusPoliciesQuery from '../FeaturedIdeaStatusPolicies.query.graphql';
import type { Idea } from '@aurora/shared-generated/types/graphql-schema-types';
import { canUpdateFeaturedWidget } from '@aurora/shared-client/helpers/nodes/NodePolicyHelper';

const log = getLog(module);

const AddFeaturedIdeaStatusesModal = dynamic(
  () => import('../AddFeaturedIdeaStatusesModal/AddFeaturedIdeaStatusesModal')
);

/**
 * Determines whether the FeaturedIdeaStatusesWidget is visible based off of the current page and node policies
 *
 * @param isPageEditorPage whether the current page is the PageEditorPage
 * @param nodePoliciesQuery the result of the nodePoliciesQuery
 * @param hasIdeaStatuses whether the widget contains ideaStatuses
 */
export function isFeaturedIdeaStatusesWidgetVisible(
  isPageEditorPage: boolean,
  ideaStatusPoliciesQuery: FeaturedIdeaStatusPoliciesQuery,
  hasIdeaStatuses: boolean
): boolean {
  const ideaBoard = ideaStatusPoliciesQuery?.coreNode as Idea;
  if (ideaBoard) {
    const isIdeaStatusEnabled = ideaBoard.ideaStatusProperties?.isIdeaStatusEnabled;
    if (isIdeaStatusEnabled) {
      if (isPageEditorPage || hasIdeaStatuses) {
        return true;
      } else {
        return canUpdateFeaturedWidget(ideaBoard);
      }
    }
  }

  return false;
}

const defaultProps: Partial<FeaturedIdeaStatusesWidgetConfigurableProps> = {
  titleSrOnly: false,
  style: IdeaStatusViewVariant.INLINE,
  listVariant: {
    type: ListVariant.LIST_GROUP
  },
  pagerVariant: {
    type: PagerVariant.LOAD_MORE
  },
  viewVariant: {
    type: IdeaStatusViewVariant.INLINE,
    props: {
      showIdeaCount: true
    }
  },
  panelType: PanelType.DIVIDER,
  showPager: true,
  lazyLoad: false,
  pageSize: 5
};

/**
 * Merge the user configured props with the default props for the widget
 * to get the final props
 * @param props user configured props
 * @returns merged final props
 */
export function getFinalProps(
  props: FeaturedIdeaStatusesWidgetConfigurableProps
): FeaturedIdeaStatusesWidgetConfigurableProps {
  return merge(defaultProps, props, {
    undefinedMergeBehavior: UndefinedValueMergeBehavior.IGNORE_BEFORE_MERGE,
    mergeNested: false
  });
}

/**
 * Renders the FeaturedIdeaStatusesWidget, which allows users to "feature" idea statuses
 * from any idea board
 *
 * @author rishabh.jivan
 */
const FeaturedIdeaStatusesWidget: InstanceScopedWidgetFC<
  FeaturedIdeaStatusesWidgetConfigurableProps
> = props => {
  const { isVisible, ...rest } = props;
  const finalProps = getFinalProps(rest);
  const {
    listVariant,
    pagerVariant,
    viewVariant,
    pageSize,
    panelType,
    className,
    instanceId,
    titleSrOnly,
    showPager
  } = finalProps;

  const cx = useClassNameMapper(localStyles);
  const {
    publicConfig: { auroraFeaturedWidgetsEnabled, messageStatusEnabled }
  } = useContext(TenantContext);
  const featuredIdeaStatusesWidgetEnabled = auroraFeaturedWidgetsEnabled && messageStatusEnabled;
  const quilt: QuiltFragment = useContext(QuiltContext);
  const pageEditorContextInterface: EditContextInterface = useContext(EditContext);
  const { contextNode } = useContext(AppContext);

  const { formatMessage, loading: textLoading } = useTranslation(
    EndUserComponent.FEATURED_IDEA_STATUSES_WIDGET
  );

  const { router, loading: routesLoading } = useEndUserRoutes();
  const isPageEditorPage: boolean = router.getCurrentPageName() === EndUserPages.PageEditorPage;

  const quiltId: string = isPageEditorPage ? pageEditorContextInterface?.quilt?.id : quilt?.id;

  const ideaStatusesQueryVariables: FeaturedIdeaStatusesWidgetQueryVariables = {
    instanceId,
    quiltId,
    ideaBoardId: contextNode.id,
    showHiddenLabel: true
  };

  const [showAddIdeaStatusesModal, setShowAddIdeaStatusesModal] = useState<boolean>(false);
  const [ideaStatusesCount, setIdeaStatusesCount] = useState<number>(-1);
  const [ideaStatusesQueryError, setIdeaStatusesQueryError] = useState<ApolloError>(null);

  const {
    data: ideaStatusPoliciesData,
    loading: ideaStatusPoliciesLoading,
    error: ideaStatusPoliciesError
  } = useQueryWithTracing<
    FeaturedIdeaStatusPoliciesQuery,
    FeaturedIdeaStatusPoliciesQueryVariables
  >(module, featuredIdeaStatusPoliciesQuery, {
    variables: {
      id: contextNode.id,
      useIdeaStatusProperties: true,
      canManageFeaturedWidget: true,
      canUpdateFeaturedWidget: true
    },
    skip: !featuredIdeaStatusesWidgetEnabled
  });

  /**
   * Updates the ideaStatuses and last modified history in component state
   *
   * @param ideaStatuses the ideaStatuses returned by the FeaturedIdeaStatusesWidget query
   * @param lastModHistory the last modified date and user for the widget
   */
  const onIdeaStatusesQueryUpdate = useCallback(
    (
      queryResult: QueryResult<
        FeaturedIdeaStatusesWidgetQuery,
        FeaturedIdeaStatusesWidgetQueryVariables
      >
    ) => {
      const { data, error } = queryResult;
      setIdeaStatusesQueryError(error);
      if (data) {
        const {
          featuredStatusesWidget: {
            statuses: { totalCount }
          }
        } = data;
        setIdeaStatusesCount(totalCount);
      }
    },
    []
  );

  /**
   * As and when config values, settings and the status list are available,
   * we have to recalculate whether the widget is allowed to be visible,
   * depending on the configs, policies and whether the list is empty or not
   */
  useEffect(() => {
    if (!featuredIdeaStatusesWidgetEnabled) {
      isVisible(false);
    } else if (!ideaStatusPoliciesLoading && !isPageEditorPage && ideaStatusesCount !== -1) {
      isVisible(
        isFeaturedIdeaStatusesWidgetVisible(
          isPageEditorPage,
          ideaStatusPoliciesData,
          ideaStatusesCount > 0
        )
      );
    }
  }, [
    featuredIdeaStatusesWidgetEnabled,
    isPageEditorPage,
    isVisible,
    ideaStatusesCount,
    ideaStatusPoliciesData,
    ideaStatusPoliciesLoading,
    textLoading
  ]);

  /**
   * Toggles the visibility of the AddFeaturedIdeaStatusesModal
   */
  function toggleModal(): void {
    setShowAddIdeaStatusesModal(previousState => !previousState);
  }

  const title: string = formatMessage('title');

  if (ideaStatusPoliciesError) {
    log.error(
      'error checking policies for FeaturedIdeaStatusesWidget',
      ideaStatusPoliciesError.message
    );
  }

  if (ideaStatusesQueryError) {
    log.error(
      `error retrieving ideaStatuses for FeaturedIdeaStatusesWidget with instance id ${instanceId}`,
      ideaStatusesQueryError.message
    );
  }

  if (
    !featuredIdeaStatusesWidgetEnabled ||
    (!isPageEditorPage &&
      typeof ideaStatusesCount === 'number' &&
      ideaStatusesCount === 0 &&
      !canUpdateFeaturedWidget(ideaStatusPoliciesData?.coreNode as Idea)) ||
    textLoading ||
    routesLoading ||
    ideaStatusPoliciesLoading ||
    ideaStatusPoliciesError ||
    ideaStatusesQueryError
  ) {
    return null;
  }

  /**
   * Renders the edit button which allows users to update the ideaStatuses from the enduser application
   */
  function renderEditButton(): React.ReactElement {
    return (
      <Button
        className={cx('lia-edit-btn')}
        testId="FeaturedIdeaStatusesWidget.Edit.Button"
        variant={ButtonVariant.NO_VARIANT}
        onClick={() => setShowAddIdeaStatusesModal(previousState => !previousState)}
        aria-label={formatMessage('edit')}
      >
        <Icon icon={Icons.EditIcon} size={IconSize.PX_16} color={IconColor.GRAY_900} />
      </Button>
    );
  }

  return (
    <EditableWidget<InstanceScopedWidgetProps> props={finalProps}>
      <AddFeaturedIdeaStatusesModal
        show={showAddIdeaStatusesModal}
        onHide={() => setShowAddIdeaStatusesModal(false)}
        ideaStatusesQueryVariables={ideaStatusesQueryVariables}
      />
      <div className={cx('position-relative')}>
        {!isPageEditorPage &&
          ideaStatusesCount > 0 &&
          canUpdateFeaturedWidget(ideaStatusPoliciesData?.coreNode as Idea) &&
          renderEditButton()}
        <FeaturedIdeaStatusesList
          ideaStatusesQueryVariables={ideaStatusesQueryVariables}
          className={className}
          toggleModal={toggleModal}
          onIdeaStatusesQueryUpdate={onIdeaStatusesQueryUpdate}
          viewVariant={viewVariant}
          listVariant={listVariant}
          panelType={panelType}
          pagerVariant={pagerVariant}
          header={{ title, titleSrOnly }}
          showPager={showPager}
          pageSize={pageSize}
        />
      </div>
    </EditableWidget>
  );
};
export default FeaturedIdeaStatusesWidget;
